import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters, isWorkspaceAdmin, userHasPermission } from '../../../utils/permissions';
import { settingsBase } from './settingsPaths';
import '../tender/CustomerMaster.scss';
import '../tender/PersonnelPage.scss';

const NotificationsSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const base = settingsBase(location.pathname);
  const canCompany = isWorkspaceAdmin(user?.roles)
    || userHasPermission(user?.permissions, 'platform.company.manage')
    || userHasPermission(user?.permissions, 'company.view')
    || userHasPermission(user?.permissions, 'company.update');
  const [emailOn, setEmailOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useDocumentTitle('Notifications');

  useEffect(() => {
    if (!canCompany) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    platformAPI.getCompany()
      .then((response) => {
        if (cancelled) return;
        const settings = response.data?.data?.company?.settings || {};
        setEmailOn(settings.notifications?.email !== false);
      })
      .catch((error) => {
        if (!cancelled) setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to load notification settings.' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [canCompany]);

  const save = async (event) => {
    event.preventDefault();
    if (!canCompany) return;
    setSaving(true);
    setMessage(null);
    try {
      const current = await platformAPI.getCompany();
      const company = current.data?.data?.company || {};
      const settings = {
        ...(company.settings || {}),
        notifications: {
          ...(company.settings?.notifications || {}),
          email: emailOn
        }
      };
      await platformAPI.updateCompany({ settings });
      setMessage({ variant: 'success', text: 'Notification preferences saved.' });
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to save notification preferences.' });
    } finally {
      setSaving(false);
    }
  };

  if (!canManageMasters(user?.permissions, user?.roles) && !canCompany) {
    return <Alert variant="warning">A tender administrator opens this page.</Alert>;
  }

  if (loading) {
    return (
      <div className="py-5 text-center text-muted">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading notifications…
      </div>
    );
  }

  return (
    <div className="cust-page">
      <form className="cust-record pers-record" onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate(base)}>
              <BiArrowBack />
              Settings
            </button>
            <div className="cust-mast__row">
              <span className="cust-mark" aria-hidden>N</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">Settings</p>
                <h1>Notifications</h1>
                <div className="cust-mast__meta">
                  <span className="cust-chip">{emailOn ? 'Email on' : 'Email off'}</span>
                </div>
              </div>
            </div>
          </div>
          {canCompany ? (
            <div className="cust-mast__actions">
              <Button size="sm" variant="primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          ) : null}
        </header>

        {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}

        <section className="cust-section">
          <div className="cust-section__head">
            <span>01</span>
            <div>
              <h2>Channels</h2>
              <p>Choose how the organisation hears about workspace events. Outbound mail still uses the Email settings.</p>
            </div>
          </div>
          <div className="cust-fields">
            <Form.Group className="cust-field cust-field--12">
              {canCompany ? (
                <Form.Check
                  type="switch"
                  id="notify-email"
                  label={emailOn ? 'Email notices may be sent' : 'Email notices are switched off'}
                  checked={emailOn}
                  onChange={(event) => setEmailOn(event.target.checked)}
                />
              ) : (
                <Alert variant="info" className="mb-0">
                  A platform administrator sets the organisation channel. Mail delivery is configured under Email.
                </Alert>
              )}
            </Form.Group>
          </div>
        </section>

        <section className="cust-section">
          <div className="cust-section__head">
            <span>02</span>
            <div>
              <h2>Delivery</h2>
              <p>The mailbox that carries notices lives on the Email card.</p>
            </div>
          </div>
          <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate(`${base}/email`)}>
            Open Email settings
          </Button>
        </section>
      </form>
    </div>
  );
};

export default NotificationsSettingsPage;
